import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { getDocs, collection } from "firebase/firestore";
import { db } from "../firebase";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { trackCategoryFilter } from "../utils/analytics";

// Animated background grid
const GridBackground = () => (
  <div className="fixed inset-0 overflow-hidden pointer-events-none">
    <div
      className="absolute inset-0 opacity-[0.02]"
      style={{
        backgroundImage: `
          linear-gradient(rgba(168, 114, 14, 0.5) 1px, transparent 1px),
          linear-gradient(90deg, rgba(168, 114, 14, 0.5) 1px, transparent 1px)
        `,
        backgroundSize: "60px 60px",
      }}
    />
  </div>
);

// Floating orbs background
const FloatingOrbs = () => (
  <div className="fixed inset-0 overflow-hidden pointer-events-none">
    <motion.div
      className="absolute w-[600px] h-[600px] rounded-full opacity-30"
      style={{
        background: "rgba(232, 180, 76, 0.10)",
        left: "-10%",
        top: "10%",
      }}
      animate={{ x: [0, 50, 0], y: [0, 30, 0] }}
      transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
    />
    <motion.div
      className="absolute w-[500px] h-[500px] rounded-full opacity-30"
      style={{
        background: "rgba(232, 180, 76, 0.08)",
        right: "-5%",
        bottom: "20%",
      }}
      animate={{ x: [0, -30, 0], y: [0, -50, 0] }}
      transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
    />
    <motion.div
      className="absolute w-[400px] h-[400px] rounded-full opacity-20"
      style={{
        background: "rgba(15, 107, 191, 0.06)",
        left: "40%",
        top: "60%",
      }}
      animate={{ x: [0, 40, 0], y: [0, -30, 0] }}
      transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
    />
  </div>
);

// Category icons
const CategoryIcon = ({ category }) => {
  const icons = {
    AI: (
      <svg
        className="w-4 h-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
        />
      </svg>
    ),
    Technology: (
      <svg
        className="w-4 h-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
        />
      </svg>
    ),
    Tutorial: (
      <svg
        className="w-4 h-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
        />
      </svg>
    ),
    Update: (
      <svg
        className="w-4 h-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M13 10V3L4 14h7v7l9-11h-7z"
        />
      </svg>
    ),
  };
  return icons[category] || icons.Technology;
};

// Helper function to generate URL slug from title
const generateSlug = (title) => {
  return (title || "")
    .toLowerCase()
    .replace(/[^\w\s-]/g, "") // Remove special characters
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/-+/g, "-") // Replace multiple hyphens with single hyphen
    .substring(0, 60); // Limit length
};

// Blog card component
const BlogCard = ({ item, idx }) => {
  const navigate = useNavigate();
  const getCategoryStyle = (category) => {
    const styles = {
      AI: {
        gradient: "bg-[#E8B44C]/15",
        border: "border-[#E1DCCE] hover:border-[#D9A32E]",
        glow: "group-hover:shadow-[#E8B44C]/20",
        badge: "bg-[#F1EEE5] text-[#A8720E] border-[#E1DCCE]",
        accent: "text-[#A8720E]",
      },
      Technology: {
        gradient: "bg-[#E8B44C]/15",
        border: "border-[#E1DCCE] hover:border-[#D9A32E]",
        glow: "group-hover:shadow-[#E8B44C]/20",
        badge: "bg-[#F1EEE5] text-[#A8720E] border-[#E1DCCE]",
        accent: "text-[#A8720E]",
      },
      Tutorial: {
        gradient: "bg-[#E8B44C]/15",
        border: "border-[#E1DCCE] hover:border-[#D9A32E]",
        glow: "group-hover:shadow-[#E8B44C]/20",
        badge: "bg-[#F1EEE5] text-[#A8720E] border-[#E1DCCE]",
        accent: "text-[#A8720E]",
      },
      Update: {
        gradient: "bg-[#E8B44C]/15",
        border: "border-[#E1DCCE] hover:border-[#D9A32E]",
        glow: "group-hover:shadow-[#E8B44C]/20",
        badge: "bg-[#F1EEE5] text-[#A8720E] border-[#E1DCCE]",
        accent: "text-[#A8720E]",
      },
    };
    return styles[category] || styles.Technology;
  };

  const style = getCategoryStyle(item.category);

  // Extract excerpt from markdown
  const extractExcerpt = (markdown) => {
    // Remove markdown syntax and get plain text
    const plainText = markdown
      .replace(/^#+ .+$/gm, "") // Remove headings
      .replace(/\*\*(.+?)\*\*/g, "$1") // Remove bold
      .replace(/\*(.+?)\*/g, "$1") // Remove italic
      .replace(/\[(.+?)\]\(.+?\)/g, "$1") // Remove links
      .replace(/`(.+?)`/g, "$1") // Remove inline code
      .replace(/^\s*[-*+]\s+/gm, "") // Remove list markers
      .trim();
    return plainText.substring(0, 150) + (plainText.length > 150 ? "..." : "");
  };

  const title = item.title || "Untitled Post";
  const excerpt = extractExcerpt(item.text);
  const slug = generateSlug(item.title);

  const handleClick = () => {
    navigate(`/blog/${slug}`, { state: { blogId: item.firestoreId } });
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.08, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="group relative"
      onClick={handleClick}
    >
      {/* Glow effect */}
      <div
        className={`absolute -inset-[1px] ${style.gradient} rounded-2xl opacity-0 group-hover:opacity-100 blur-xl transition-all duration-500 ${style.glow}`}
      />

      {/* Card */}
      <div
        className={`relative h-full bg-white shadow-[0_1px_2px_rgba(52,44,26,0.06),0_4px_14px_rgba(52,44,26,0.05)] rounded-2xl border ${style.border} p-6 transition-all duration-500 group-hover:translate-y-[-4px] group-hover:shadow-lg cursor-pointer overflow-hidden`}
      >
        {/* Decorative corner */}
        <div className="absolute top-0 right-0 w-24 h-24 pointer-events-none opacity-50">
          <div
            className={`absolute top-3 right-3 w-12 h-[1px] ${style.accent} bg-current`}
          />
          <div
            className={`absolute top-3 right-3 w-[1px] h-12 ${style.accent} bg-current`}
          />
        </div>

        {/* Category badge */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-full border ${style.badge}`}
            >
              <CategoryIcon category={item.category} />
              {item.category}
            </span>
            {item.isFeatured && (
              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full border bg-[#F1EEE5] text-[#A8720E] border-[#D9A32E]">
                <svg
                  className="w-3 h-3"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                Featured
              </span>
            )}
          </div>
          <span className="text-xs text-[#8A93A2] font-mono">
            {new Date(item.createdAt).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })}
          </span>
        </div>

        {/* Title */}
        <h3
          className={`text-lg font-bold text-[#26313B] mb-3 group-hover:${style.accent} transition-colors line-clamp-2`}
        >
          {title}
        </h3>

        {/* Excerpt */}
        <p className="text-[#5B6B7C] text-sm leading-relaxed line-clamp-3 mb-4">
          {excerpt}
        </p>

        {/* Read more */}
        <div className="flex items-center justify-between pt-4 border-t border-[#E1DCCE]">
          <span
            className={`text-sm font-medium ${style.accent} group-hover:underline underline-offset-4`}
          >
            Read article
          </span>
          <motion.div
            className={style.accent}
            animate={{ x: [0, 5, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </motion.div>
        </div>

        {/* Animated border glow on hover */}
        <motion.div
          className="absolute inset-0 rounded-2xl pointer-events-none opacity-0 group-hover:opacity-100"
          style={{
            background: "rgba(232, 180, 76, 0.06)",
          }}
          animate={{ x: ["-100%", "100%"] }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        />
      </div>
    </motion.article>
  );
};

const Blogs = () => {
  const { currentUser } = useAuth();
  const [items, setItems] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const itemsPerPage = 6;

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const snapshot = await getDocs(collection(db, "Blogs"));
        const list = snapshot.docs.map((docItem) => ({
          firestoreId: docItem.id,
          title: docItem.data().blog_title || "",
          text: docItem.data().blog_contetnt,
          category: docItem.data().blog_category || "Uncategorized",
          createdAt: docItem.data().createdAt || Date.now(),
          isFeatured: docItem.data().isFeatured || false,
        }));

        list.sort((a, b) => b.createdAt - a.createdAt);

        setItems(list);
        setFiltered(list);

        const uniqueCategories = [
          "All",
          ...new Set(list.map((i) => i.category)),
        ];
        setCategories(uniqueCategories);
      } catch (error) {
        console.error("Error fetching blogs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchItems();
  }, []);

  const handleFilter = (cat) => {
    setActiveCategory(cat);
    trackCategoryFilter(cat, currentUser);
    if (cat === "All") {
      setFiltered(items);
    } else {
      setFiltered(items.filter((i) => i.category === cat));
    }
    setCurrentPage(1);
  };

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentItems = filtered.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filtered.length / itemsPerPage);

  return (
    <div className="min-h-screen bg-[#FBFAF6] text-[#26313B]">
      <Helmet>
        <title>Cosmovex Tech Blog — Developer Tutorials, AI Insights & Mobile Development</title>
        <meta name="title" content="Cosmovex Tech Blog — Developer Tutorials, AI Insights & Mobile Development" />
        <meta name="description" content="Read our tech blog for in-depth tutorials on Android development, mobile tools, markdown editing, SSH terminals, JSON formatting, APIs, and more. Expert insights from the Cosmovex team." />
        <meta name="keywords" content="developer blog, android development blog, mobile development tutorials, markdown guide, SSH terminal tutorial, JSON formatting, API development, mobile tools" />
        <link rel="canonical" href="https://cosmovexapps.com/blogs" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cosmovexapps.com/blogs" />
        <meta property="og:title" content="Cosmovex Tech Blog — Developer Tutorials & Insights" />
        <meta property="og:description" content="Expert articles on Android development, mobile tools, and developer productivity." />
        <meta property="twitter:card" content="summary" />
        <meta property="twitter:url" content="https://cosmovexapps.com/blogs" />
        <meta property="twitter:title" content="Cosmovex Tech Blog" />
        <meta property="twitter:description" content="Developer tutorials and insights from Cosmovex." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "Cosmovex Tech Blog",
            "url": "https://cosmovexapps.com/blogs",
            "description": "Developer tutorials, mobile development guides, and tech insights from Cosmovex",
            "publisher": {
              "@type": "Organization",
              "name": "Cosmovex",
              "url": "https://cosmovexapps.com"
            }
          })}
        </script>
      </Helmet>
      <GridBackground />
      <FloatingOrbs />

      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#FBFAF6]/80 backdrop-blur-xl border-b border-[#E1DCCE]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative">
              <div className="w-10 h-10 bg-[#E8B44C] border border-[#D9A32E] rounded-xl flex items-center justify-center shadow-lg shadow-[#E8B44C]/30 group-hover:shadow-[#E8B44C]/50 transition-shadow">
                <span className="text-lg font-black text-[#221A06]">C</span>
              </div>
              <motion.div className="absolute -inset-1 bg-[#E8B44C] rounded-xl opacity-0 group-hover:opacity-30 blur-md transition-opacity" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#26313B] group-hover:text-[#A8720E] transition-colors">
                Cosmovex
              </h1>
              <p className="text-xs text-[#8A93A2] font-mono">// blog</p>
            </div>
          </Link>

          <nav className="flex items-center gap-2">
            <Link
              to="/"
              className="px-4 py-2 text-sm text-[#5B6B7C] hover:text-[#26313B] hover:bg-[#F1EEE5] rounded-lg transition-all"
            >
              Home
            </Link>
            <Link
              to="/blogs"
              className="px-4 py-2 text-sm text-[#221A06] bg-[#E8B44C] border border-[#D9A32E] rounded-lg font-medium"
            >
              Blogs
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 px-6 overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <motion.div
            className="w-[800px] h-[400px] rounded-full opacity-30"
            style={{
              background: "rgba(232, 180, 76, 0.08)",
            }}
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        <div className="max-w-7xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#F1EEE5] border border-[#E1DCCE] rounded-full mb-6"
          >
            <span className="w-2 h-2 rounded-full bg-[#A8720E] animate-pulse" />
            <span className="text-sm text-[#A8720E] font-medium">
              Tech Insights & Updates
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="text-5xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight"
          >
            The{" "}
            <span className="text-[#A8720E]">
              Cosmovex
            </span>{" "}
            Blog
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-[#5B6B7C] text-lg md:text-xl max-w-2xl mx-auto mb-8 leading-relaxed"
          >
            Deep dives into AI, cutting-edge technology, tutorials, and product
            updates from the Cosmovex team.
          </motion.p>
        </div>
      </section>

      {/* Category Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.6 }}
        className="max-w-7xl mx-auto px-6 mb-12"
      >
        <div className="flex flex-wrap gap-3 justify-center">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => handleFilter(cat)}
              className={`relative px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 ${
                cat === activeCategory
                  ? "text-[#221A06]"
                  : "text-[#5B6B7C] hover:text-[#26313B] bg-white hover:bg-[#F1EEE5] border border-[#E1DCCE]"
              }`}
            >
              {cat === activeCategory && (
                <motion.div
                  layoutId="activeCategoryBg"
                  className="absolute inset-0 bg-[#E8B44C] border border-[#D9A32E] rounded-xl"
                  transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                />
              )}
              <span className="relative z-10 flex items-center gap-2">
                {cat !== "All" && <CategoryIcon category={cat} />}
                {cat}
              </span>
            </button>
          ))}
        </div>
      </motion.div>

      {/* Blogs Grid */}
      <div className="max-w-7xl mx-auto px-6 pb-20">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <motion.div
              className="w-12 h-12 border-3 border-[#E8B44C]/40 border-t-[#A8720E] rounded-full"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
          </div>
        ) : currentItems.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-[#F1EEE5] flex items-center justify-center">
              <svg
                className="w-10 h-10 text-[#A8720E]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <p className="text-[#5B6B7C] text-lg">
              No articles found in this category.
            </p>
            <p className="text-[#8A93A2] text-sm mt-2">
              Check back soon for new content!
            </p>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {currentItems.map((item, idx) => (
              <BlogCard key={item.firestoreId} item={item} idx={idx} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="flex items-center justify-center gap-2 mt-16"
          >
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="w-10 h-10 rounded-xl bg-white hover:bg-[#F1EEE5] border border-[#E1DCCE] flex items-center justify-center transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
              <button
                key={num}
                onClick={() => setCurrentPage(num)}
                className={`relative w-10 h-10 rounded-xl font-medium transition-all ${
                  currentPage === num
                    ? "text-[#221A06]"
                    : "text-[#5B6B7C] hover:text-[#26313B] bg-white hover:bg-[#F1EEE5] border border-[#E1DCCE]"
                }`}
              >
                {currentPage === num && (
                  <motion.div
                    layoutId="activePageBg"
                    className="absolute inset-0 bg-[#E8B44C] border border-[#D9A32E] rounded-xl"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                  />
                )}
                <span className="relative z-10">{num}</span>
              </button>
            ))}

            <button
              onClick={() =>
                setCurrentPage(Math.min(totalPages, currentPage + 1))
              }
              disabled={currentPage === totalPages}
              className="w-10 h-10 rounded-xl bg-white hover:bg-[#F1EEE5] border border-[#E1DCCE] flex items-center justify-center transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </motion.div>
        )}
      </div>

      {/* Footer */}
      <footer className="border-t border-[#E1DCCE] py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-[#8A93A2] text-sm mb-4">
            &copy; {new Date().getFullYear()} Cosmovex. All rights reserved.
          </p>
          <div className="flex justify-center gap-6 flex-wrap">
            <Link to="/privacy-policy" className="text-[#0F6BBF] hover:text-[#26313B] text-sm transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms-of-service" className="text-[#0F6BBF] hover:text-[#26313B] text-sm transition-colors">
              Terms of Service
            </Link>
            <Link to="/about" className="text-[#0F6BBF] hover:text-[#26313B] text-sm transition-colors">
              About
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Blogs;
